import React from 'react';

import { Link } from 'gatsby';
import marked from 'marked';
import slugify from 'slugify';

import ShopViewPriceBlock from './shop-view-price-block';
import AdditionalPhotos from '../controls/additional-photos';
import OutboundLinks from '../controls/outbound-links';
import PrintFramesControl from '../controls/print-frames-control';
import PrintImageContainer from '../controls/print-image-container';
import PrintSizesControl from '../controls/print-sizes-control';

import PrintProduct from '../products/print-product';

import { QuestionMarkSvg } from '../../common/svg';

const widthKeys = {
	'S': 'Width__Small_',
	'M': 'Width__Medium_',
	'L': 'Width__Large_',
	'XL': 'Width__X_Large_',
};
const heightKeys = {
	'S': 'Height__Small_',
	'M': 'Height__Medium_',
	'L': 'Height__Large_',
	'XL': 'Height__X_Large_',
};

class PrintShopView extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			size: props.data.Sizes[0],
			frame: 'None',
			quantity: 1,
			showCurrencyRequired: false,
		};

		this.calculatedPrice = this.calculatedPrice.bind(this);
		this.changeFrame = this.changeFrame.bind(this);
		this.changeSize = this.changeSize.bind(this);
		this.compiledId = this.compiledId.bind(this);
		this.description = this.description.bind(this);
		this.imageSize = this.imageSize.bind(this);
		this.totalSize = this.totalSize.bind(this);
	}

	calculatedBulkDiscount() {
		let bulkDiscount;

		switch (this.props.cartCurrency) {
			case 'usd':
				bulkDiscount = '$10';
				break;
			case 'gbp':
				bulkDiscount = '£8';
				break;
			case 'eur':
				bulkDiscount = '9€';
				break;
			case 'cad':
				bulkDiscount = 'Can$14';
				break;
			case 'aud':
				bulkDiscount = 'AU$15';
				break;
			default:
				bulkDiscount = '$10';
		}

		return bulkDiscount;
	}

	calculatedPrice() {
		const calculatedPrices = PrintProduct.calculatedPrice(
			this.state.size,
			this.state.frame,
			this.props.data.Price_Profile[0].data.Conversions
		);

		if (typeof window !== 'undefined' && window.Snipcart) {
			const cart = window.Snipcart.store.getState().cart;

			return calculatedPrices[cart.currency];
		} else {
			return null;
		}
	}

	changeFrame(frame) {
		this.setState({ frame: frame });
	};

	changeSize(size) {
		this.setState({ size: size });
	}

	compiledId() {
		return PrintProduct.compiledId(
			this.props.data.Reference,
			this.state.size,
			this.state.frame
		);
	}

	componentDidMount() {
		document.addEventListener('snipcart.ready', () => this.forceUpdate());
	}

	description() {
		return PrintProduct.description(
			this.state.size,
			this.state.frame
		);
	}

	imageSize() {
		const width = parseFloat(this.props.data[widthKeys[this.state.size]]).toFixed(1);
		const height = parseFloat(this.props.data[heightKeys[this.state.size]]).toFixed(1);

		// Re-parsing the float and letting browser convert back to string causes
		// trailing '.0' on integer values to be dropped
		return `${parseFloat(width)}" x ${parseFloat(height)}"`;
	}

	totalSize() {
		const width = parseFloat(this.props.data[widthKeys[this.state.size]]).toFixed(1);
		const height = parseFloat(this.props.data[heightKeys[this.state.size]]).toFixed(1);

		const delta = (this.state.frame === 'None') ? 2 : 5.5;

		return `${parseFloat(width) + delta}" x ${parseFloat(height) + delta}"`;
	}

	render() {
		return (
			<>
				<div className="product-view__main-view">
					<div>
						<PrintImageContainer
							data={this.props.data}
							size={this.state.size}
							frame={this.state.frame}
							showLightbox={this.props.showLightbox}
						/>
						{(this.props.data.Gallery_Image_Paths) ? (
							<AdditionalPhotos
								paths={this.props.data.Gallery_Image_Paths}
								showLightbox={this.props.showLightbox}
							/>
						) : null}
						<OutboundLinks productData={this.props.data} format="double-column" />
					</div>
					<div>
						<div className="product-view__controls-container">
							<div className="product-view__title-block">
								<p
									className="product-view__product-title"
									dangerouslySetInnerHTML={{ __html: marked(this.props.data.Name).replace('<p>','').replace('</p>','') }}
								/>
								<p className="product-view__product-subtitle">
									<Link to={`/shop/fine-art-prints/artist/${slugify(this.props.data.Artist.toLowerCase())}`}>
										{this.props.data.Artist}
										{(this.props.data.Year) ? (
											<>, {this.props.data.Year}</>
										) : null}
									</Link>
								</p>
							</div>

							<div className="product-view__meta-block">
								<PrintSizesControl
									size={this.state.size}
									data={this.props.data}
									onChange={this.changeSize}
									showModalWithContent={this.props.showModalWithContent}
								/>
							</div>

							<div className="product-view__meta-block">
								<span className="product-view__meta-label">
									Paper Type{' '}
									<button
										className="icon-link"
										onClick={() => this.props.showModalWithContent(
											<>
											<p className="header">Paper Type</p>
											<p className="body-text">
												All images are printed using archival inks on museum quality premium fine art paper — 100% cotton, finely textured, and acid-free archival substrates purposefully chosen for optimal historical art reproduction. Orders for US and Canada will be printed on Epson’s Somerset Velvet (255gsm); Australian orders on Hahnemühle’s Photo Rag (188gsm); and all other orders on St Cuthbert Mill’s Somerset Enhanced Velvet (255gsm).
											</p>
											</>
										)}
									>
										{ QuestionMarkSvg }
									</button>
								</span>
								<div className="product-view__meta-value">
									Premium fine art paper: 100% cotton, acid-free, archival
								</div>
							</div>

							<div className="product-view__meta-block">
								<PrintFramesControl
									frame={this.state.frame}
									onChange={this.changeFrame}
									showModalWithContent={this.props.showModalWithContent}
								/>
							</div>

							<div className="product-view__meta-block">
								<span className="product-view__meta-label">Overview</span>
								<div className="product-view__meta-value">
									<ul>
										<li>
											Archival inks (Giclée process) on premium fine art paper (100% cotton, acid-free).
										</li>
										<li>
											{(this.state.frame === 'None') ? (
												<>1" border</>
											) : (
												<>Solid wood frame / 2" natural white 4 ply archival matte / Shatterproof premium UV acrylic glazing / Acid-free foam core backing / Ready to hang</>
											)}
										</li>
										<li>
											Image Size: {this.imageSize()} – Total Size: {this.totalSize()}
										</li>
									</ul>
								</div>
							</div>

							{(this.props.loading) ? (
								<div className="product-view__meta-block">
									<div className="product-view__meta-value">
										Loading Prices…
									</div>
								</div>
							) : (
								<>
									<ShopViewPriceBlock
										data={ this.props.data }
										formattedPrice={ this.props.formattedPrice }
										calculatedPrice={ this.calculatedPrice }
										onMultiCurrencyChange={ this.props.onMultiCurrencyChange }
										cartCurrency={ this.props.cartCurrency }
									/>

									<div className="product-view__meta-block">
										<div className="product-view__meta-value">
											<ul>
												<li>Includes <b>Free Shipping</b> (to US, UK, EU, Canada, or Australia.)</li>
												<li>Buy 3 or more unframed prints, get a <b>{this.calculatedBulkDiscount()} discount</b> per print. (Discount applied at checkout.)</li>
											</ul>
										</div>
									</div>

									{(this.props.data.Unavailable) ? (
										<div className="product-view__meta-block">
											<div className="product-view__meta-value">
												We’re sorry. This product is temporarily unavailable.
											</div>
										</div>
									) : (
										<>
											<div className="product-view__meta-block">
												<span className="product-view__meta-label">Quantity</span>
												<span className="product-view__meta-value">
													<input
														type="number"
														name="quantity"
														value={ this.state.quantity }
														onChange={ (e) => this.setState({ quantity: e.target.value }) }
													/>
												</span>
											</div>

											{(this.props.cartCurrency) ? (
												<div>
													<button
														className="product-view__buy-button snipcart-add-item"
														data-item-id={this.compiledId()}
														data-item-name={this.props.data.Name}
														data-item-price={
															(this.props.data.Discount_Price_Profile) ? (
																this.props.serializedPrices(this.calculatedPrice(this.props.data.Discount_Price_Profile[0]))
															) : (
																this.props.serializedPrices(this.calculatedPrice(this.props.data.Price_Profile[0]))
															)}
														data-item-url={`${process.env.GATSBY_SERVER_ROOT}/data/products/${this.props.data.Reference}.json`}
														data-item-image={`https://the-public-domain-review.imgix.net${this.props.data.Image_File_Path}?w=240&h=240&fit=max`}
														data-item-description={this.description()}
														data-item-categories={(this.state.frame === 'None') ? "prints|unframedPrints" : "prints|framedPrints"}
														data-item-quantity={this.state.quantity}
													>
														Add to Cart
													</button>
												</div>
											) : (
												<div>
													<button
														className="product-view__disabled-buy-button"
														onClick={(e) => this.setState({ showCurrencyRequired: true })}
													>
														Add to Cart
													</button>
													{(this.state.showCurrencyRequired) ? (
														<span className="product-view__currency-required">
															Select a currency above to add to cart.
														</span>
													) : null}
												</div>
											)}
										</>
									)}
								</>
							)}
							<OutboundLinks productData={this.props.data} format="single-column" />
						</div>
					</div>
				</div>
			</>
		);
	}
}

export default PrintShopView;
